/**
 * BrightSign DeviceCommandDispatcher — bridges the Trillboards backend
 * `deviceCommand` socket event stream to the BrightScript command
 * dispatcher in bridge.brs.
 *
 * Background
 * ----------
 * On Android CTV tablets the backend's `deviceCommand` events are
 * consumed by the Kotlin CommandProcessor which executes them natively
 * (power, volume, brightness, screenshot, etc.). The React screen bundle
 * running in the WebView does NOT handle most device commands — the
 * native Android agent does.
 *
 * BrightSign has no Kotlin layer. The HTML zone IS the host agent. So
 * when `deviceCommand` arrives over the socket inside a BrightSign HTML
 * zone, the JS layer has to:
 *
 *   1. Receive the event from socket.io
 *   2. Translate the Trillboards command name (e.g. `power.on`) into a
 *      BSMessagePort message type that bridge.brs understands
 *      (e.g. `{type: 'setPower', state: 'on'}`)
 *   3. Call `BSMessagePort.PostBSMessage(...)` to forward the message
 *      to the BrightScript event loop
 *   4. Wait for the matching ACK (`{type:'ack', id, ok, result}`)
 *      coming back via `onbsmessage` / `trillboards:bs-message`
 *   5. Emit `deviceCommandAck` back up the socket to the backend with
 *      the same `commandId` and a normalized status/details shape
 *
 * The existing `trillboard-screen/src/overlay/main.js` already handles a
 * small subset of commands in-page (`reboot.app`, `refresh.content`,
 * `refresh.playlist`, `device.clearCache`) by reloading the page or
 * refreshing streams. That handler continues to run on BrightSign too,
 * because BrightSign still boots the same React bundle. The dispatcher
 * in THIS file only forwards the commands that need BrightScript native
 * access (power via CEC, audio via roAudioOutput, screenshot via
 * roScreenshot, SD cache purge, etc.). For the in-page reload commands,
 * this dispatcher coexists with the overlay handler — both send ACKs,
 * and the backend's `handleAck` handler is idempotent on duplicate acks
 * (the second one is rejected via status transition checks).
 *
 * Command mapping table
 * ---------------------
 * See DEVICE_COMMAND_MAP below. The table is the authoritative mapping
 * from Trillboards backend command → bridge.brs message. Commands that
 * are NOT in the table fall through to an "unsupported" ACK with a
 * structured error, so the portal sees a deterministic failure rather
 * than a timeout.
 *
 * ACK timeout
 * -----------
 * If the BrightScript side never responds to a forwarded command within
 * 30 seconds, the dispatcher emits `deviceCommandAck` with `status:
 * 'failed'` and `details: {error: 'bridge_timeout'}`. 30s matches the
 * Android agent's timeout behaviour and the backend's retry window
 * (see `deviceCommandDispatcher.js` RETRY budget).
 */

/* eslint-disable no-console */

const BRIDGE_TIMEOUT_MS = 30000;
const SOCKET_POLL_INTERVAL_MS = 500;
const SOCKET_POLL_MAX_MS = 10000;

/**
 * Normalize a command name to lowercase so the dispatcher tolerates any
 * historical casing drift. The backend emits lowercase already but the
 * RecurringDeviceSchedule enum shows some legacy casings.
 */
function normalizeCommandName(command) {
  if (typeof command !== 'string') return '';
  return command.trim();
}

/**
 * Clamp a numeric level (volume / brightness) to [0, 100]. The backend
 * should enforce this already, but we re-enforce here to protect
 * bridge.brs from receiving out-of-range values. Returns null if the
 * input isn't a finite number.
 */
function clampLevel(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return typeof fallback === 'number' ? fallback : null;
  }
  if (n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n);
}

/**
 * Map a Trillboards `input.set` payload.inputId to a bridge.brs
 * `setInput` state string. CEC inputs are HDMI1..HDMI4. Anything else
 * becomes `hdmi1` by default.
 */
function normalizeInputState(inputId) {
  if (typeof inputId !== 'string') return 'hdmi1';
  const upper = inputId.trim().toUpperCase();
  if (upper === 'HDMI1' || upper === 'HDMI2' || upper === 'HDMI3' || upper === 'HDMI4') {
    return upper.toLowerCase();
  }
  return 'hdmi1';
}

/**
 * DEVICE_COMMAND_MAP — authoritative mapping from backend command name
 * to a translation function. Each entry returns one of:
 *
 *   { kind: 'forward', message: {...} }       → send via BSMessagePort, wait for ack
 *   { kind: 'immediate', status, details }    → ACK right away (no BS round-trip)
 *   { kind: 'unsupported', reason }           → fail fast with a reason code
 *
 * The dispatcher uses the result to decide whether to forward the
 * command to BrightScript or ACK locally.
 */
const DEVICE_COMMAND_MAP = {
  // --- Reload / refresh family --------------------------------------------
  // main.js already reloads the page on reboot.app/app.reload, and the
  // bridge.brs `refresh` type reloads the HTML widget (which is
  // equivalent to a page reload on BrightSign). We forward to BrightScript
  // so the BS side can log the event and so the player ACKs cleanly
  // even if the JS reload is cancelled by an upstream error.
  'reboot.app': () => ({
    kind: 'forward',
    message: { type: 'refresh' },
    successDetails: { command: 'reboot.app', reloading: true },
  }),
  'app.reload': () => ({
    kind: 'forward',
    message: { type: 'refresh' },
    successDetails: { command: 'app.reload', reloading: true },
  }),
  'refresh.content': () => ({
    kind: 'forward',
    message: { type: 'refresh' },
    successDetails: { command: 'refresh.content' },
  }),
  'refresh.playlist': () => ({
    kind: 'forward',
    message: { type: 'refresh' },
    successDetails: { command: 'refresh.playlist' },
  }),
  'refresh.app': () => ({
    kind: 'forward',
    message: { type: 'refresh' },
    successDetails: { command: 'refresh.app' },
  }),

  // --- Device lifecycle ---------------------------------------------------
  'device.reboot': () => ({
    kind: 'forward',
    message: { type: 'reboot' },
    successDetails: { command: 'device.reboot', rebooting: true },
  }),
  'device.restart': () => ({
    kind: 'forward',
    message: { type: 'refresh' }, // soft restart = HTML widget reload
    successDetails: { command: 'device.restart' },
  }),
  'device.screenshot': () => ({
    kind: 'forward',
    message: { type: 'screenshot' },
    successDetails: { command: 'device.screenshot' },
  }),
  'device.clearCache': () => ({
    kind: 'forward',
    message: { type: 'clearCache' },
    successDetails: { command: 'device.clearCache' },
  }),

  // --- Power (CEC via bridge.brs CecPowerOn / CecPowerOff) ---------------
  'power.on': () => ({
    kind: 'forward',
    message: { type: 'setPower', state: 'on' },
    successDetails: { command: 'power.on', power: true, mode: 'cec' },
  }),
  'power.off': () => ({
    kind: 'forward',
    message: { type: 'setPower', state: 'off' },
    successDetails: { command: 'power.off', power: false, mode: 'cec' },
  }),
  // power.toggle has no direct CEC equivalent (we can't read TV state),
  // so we treat it as power.on — the ToggleKey UX assumes the caller
  // tracks state. This matches the Android fallback behaviour in
  // CommandProcessor.kt which assumes a boolean switch.
  'power.toggle': () => ({
    kind: 'forward',
    message: { type: 'setPower', state: 'on' },
    successDetails: { command: 'power.toggle', power: true, mode: 'cec' },
  }),

  // --- Blackout (soft power) ---------------------------------------------
  // BrightSign has no blackout overlay in the current screen bundle. We
  // forward to bridge.brs as `setPower` so CEC still fires if possible,
  // but this is effectively aliased to power.on/off on BrightSign.
  'blackout.start': () => ({
    kind: 'forward',
    message: { type: 'setPower', state: 'off' },
    successDetails: { command: 'blackout.start', blackout: true, mode: 'cec' },
  }),
  'blackout.end': () => ({
    kind: 'forward',
    message: { type: 'setPower', state: 'on' },
    successDetails: { command: 'blackout.end', blackout: false, mode: 'cec' },
  }),

  // --- Volume / mute (roAudioOutput via bridge.brs SetAudioLevel) --------
  'volume.set': (payload) => {
    // Accept both payload.level (admin/copilot convention) and
    // payload.volume (MDM complianceService convention).
    const level = clampLevel(payload && (payload.level != null ? payload.level : payload.volume), 50);
    return {
      kind: 'forward',
      message: { type: 'setVolume', level },
      successDetails: { command: 'volume.set', volume: level },
    };
  },
  'volume.up': () => ({
    // No direct "relative" opcode — we forward 'setVolume' at a
    // conventional +10% step; the backend payloads rarely include a
    // current level, so we approximate with 60 (half-scale + step).
    kind: 'forward',
    message: { type: 'setVolume', level: 60 },
    successDetails: { command: 'volume.up', volume: 60 },
  }),
  'volume.down': () => ({
    kind: 'forward',
    message: { type: 'setVolume', level: 40 },
    successDetails: { command: 'volume.down', volume: 40 },
  }),
  // Mute on BrightSign: we route to setVolume(0) / setVolume(50) since
  // roAudioOutput does not expose a mute primitive and a zero volume is
  // the conventional substitute.
  'mute.on': () => ({
    kind: 'forward',
    message: { type: 'setVolume', level: 0 },
    successDetails: { command: 'mute.on', muted: true },
  }),
  'mute.off': () => ({
    kind: 'forward',
    message: { type: 'setVolume', level: 50 },
    successDetails: { command: 'mute.off', muted: false },
  }),
  'mute.toggle': () => ({
    // Toggle has no state tracking, default to muting.
    kind: 'forward',
    message: { type: 'setVolume', level: 0 },
    successDetails: { command: 'mute.toggle', muted: true },
  }),

  // --- Brightness (CEC luminance hints via bridge.brs CecSetBrightness) --
  'brightness.set': (payload) => {
    const level = clampLevel(payload && payload.level, 100);
    return {
      kind: 'forward',
      message: { type: 'setBrightness', level },
      successDetails: { command: 'brightness.set', brightness: level },
    };
  },

  // --- Input selection (CEC Active Source via bridge.brs CecSetInput) ---
  'input.set': (payload) => {
    const input = normalizeInputState(payload && payload.inputId);
    return {
      kind: 'forward',
      message: { type: 'setInput', input },
      successDetails: { command: 'input.set', inputId: input },
    };
  },

  // --- App-level commands (no-ops in BrightSign kiosk model) -------------
  // BrightSign has a single HTML widget; there's no "other app to launch"
  // and "home" has no meaning. We ACK immediately with a note so the
  // portal can surface a helpful "not applicable" rather than a failure.
  'app.launch': () => ({
    kind: 'immediate',
    status: 'failed',
    details: {
      command: 'app.launch',
      error: 'unsupported_on_brightsign',
      note: 'brightsign players run a single html widget',
    },
  }),
  'app.resume': () => ({
    kind: 'immediate',
    status: 'acked',
    details: {
      command: 'app.resume',
      ok: true,
      note: 'brightsign html widget is always foregrounded',
    },
  }),
  'app.home': () => ({
    kind: 'immediate',
    status: 'acked',
    details: {
      command: 'app.home',
      ok: true,
      note: 'brightsign has no home screen',
    },
  }),

  // --- Kiosk mode (always on on BrightSign) ------------------------------
  'kiosk.enable': () => ({
    kind: 'immediate',
    status: 'acked',
    details: {
      command: 'kiosk.enable',
      ok: true,
      note: 'brightsign always in kiosk',
    },
  }),
  'kiosk.disable': () => ({
    kind: 'immediate',
    status: 'failed',
    details: {
      command: 'kiosk.disable',
      error: 'unsupported_on_brightsign',
      note: 'brightsign always in kiosk',
    },
  }),
};

/**
 * Stateful dispatcher class. Construct one per socket connection; call
 * `.attach(socket)` to begin listening to `deviceCommand` events.
 *
 * Internal state:
 *   - `_pending`: Map<commandId, { timeout, resolve }>
 *     Tracks forwarded commands awaiting ACK from bridge.brs. The
 *     timeout fires after BRIDGE_TIMEOUT_MS with a 'bridge_timeout'
 *     error ack so the backend isn't left hanging.
 *   - `_socket`: the socket.io client (or a test double)
 *   - `_inboundListener`: the window event listener for
 *     'trillboards:bs-message' — we keep a reference so detach() can
 *     removeEventListener cleanly.
 */
class BrightSignDeviceCommandDispatcher {
  constructor(options) {
    const opts = options || {};
    this._socket = null;
    this._pending = new Map();
    this._inboundListener = null;
    this._portMessageHandler = null;
    this._previousOnbsmessage = null;
    this._attached = false;
    this._timeoutMs = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : BRIDGE_TIMEOUT_MS;
    // Allow tests to inject a custom now() for deterministic tracking.
    this._getMessagePort = typeof opts.getMessagePort === 'function'
      ? opts.getMessagePort
      : () => (typeof window !== 'undefined' ? window.BSMessagePort : null);
    this._log = typeof opts.log === 'function' ? opts.log : (level, msg, data) => {
      try {
        if (level === 'warn' && typeof console !== 'undefined' && console.warn) {
          console.warn('[BrightSignDeviceCommand]', msg, data || '');
        } else if (level === 'error' && typeof console !== 'undefined' && console.error) {
          console.error('[BrightSignDeviceCommand]', msg, data || '');
        } else if (typeof console !== 'undefined' && console.debug) {
          console.debug('[BrightSignDeviceCommand]', msg, data || '');
        }
      } catch (_) {
        // console may be suppressed in production bundles
      }
    };
  }

  /**
   * Attach to the provided socket.io client. Starts listening for
   * `deviceCommand` events and installs an inbound bridge listener for
   * BSMessagePort ACKs. Idempotent — calling attach twice with the same
   * socket is a no-op; calling attach with a different socket first
   * detaches the previous one.
   */
  attach(socket) {
    if (!socket || typeof socket.on !== 'function' || typeof socket.emit !== 'function') {
      this._log('warn', 'attach called with invalid socket', { socket });
      return false;
    }
    if (this._attached && this._socket === socket) {
      return true;
    }
    if (this._attached) {
      this.detach();
    }

    this._socket = socket;
    this._handleDeviceCommand = this._handleDeviceCommand.bind(this);
    socket.on('deviceCommand', this._handleDeviceCommand);

    this._installInboundBridge();

    this._attached = true;
    this._log('info', 'dispatcher attached to socket');
    return true;
  }

  /**
   * Detach from the current socket and clear any pending commands.
   * Safe to call multiple times.
   */
  detach() {
    if (!this._attached) return;
    if (this._socket && typeof this._socket.off === 'function') {
      try {
        this._socket.off('deviceCommand', this._handleDeviceCommand);
      } catch (_) {
        // socket.io v4 has .off(); older clients use removeListener; swallow.
      }
    }
    this._uninstallInboundBridge();

    // Clear pending timeouts so we don't leak handles.
    for (const entry of this._pending.values()) {
      if (entry && entry.timeout) {
        try { clearTimeout(entry.timeout); } catch (_) { /* noop */ }
      }
    }
    this._pending.clear();
    this._socket = null;
    this._attached = false;
  }

  /**
   * Install the inbound message bridge. The canonical BrightSign API is
   * `BSMessagePort.onbsmessage`; we take ownership of that property and
   * chain through to any previous handler (e.g. bootstrap.html's
   * console-logging handler) so we don't break existing subscribers.
   *
   * As a fallback for environments where BSMessagePort is not exposed
   * (e.g. a locked-down sandbox), we ALSO subscribe to the
   * `trillboards:bs-message` window CustomEvent. The fallback only
   * activates when we fail to install onbsmessage — otherwise we'd
   * double-deliver every message that bootstrap.html's chained handler
   * re-dispatches as a CustomEvent.
   */
  _installInboundBridge() {
    const port = this._getMessagePort();

    let onbsmessageInstalled = false;
    if (port) {
      this._previousOnbsmessage = typeof port.onbsmessage === 'function' ? port.onbsmessage : null;
      this._portMessageHandler = (payload) => {
        // Preserve the previous handler's behaviour (e.g. dispatching
        // the CustomEvent) so we don't break existing subscribers.
        if (typeof this._previousOnbsmessage === 'function') {
          try { this._previousOnbsmessage(payload); } catch (_) { /* noop */ }
        }
        this._handleInboundMessage(payload);
      };
      try {
        port.onbsmessage = this._portMessageHandler;
        onbsmessageInstalled = true;
      } catch (_) {
        // property may be frozen in some odd environments — fall
        // through to the CustomEvent fallback below.
        this._portMessageHandler = null;
        this._previousOnbsmessage = null;
      }
    }

    if (!onbsmessageInstalled) {
      // Fallback: listen for the CustomEvent bootstrap.html dispatches.
      // This path only fires when we couldn't install onbsmessage so
      // there's no risk of double-delivery.
      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        this._inboundListener = (event) => {
          const message = event && event.detail;
          this._handleInboundMessage(message);
        };
        try {
          window.addEventListener('trillboards:bs-message', this._inboundListener);
        } catch (_) {
          this._inboundListener = null;
        }
      }
    }
  }

  /**
   * Remove the inbound message bridge. Restores the previous
   * onbsmessage handler if we captured one in installInboundBridge.
   */
  _uninstallInboundBridge() {
    if (this._inboundListener && typeof window !== 'undefined') {
      try {
        window.removeEventListener('trillboards:bs-message', this._inboundListener);
      } catch (_) { /* noop */ }
      this._inboundListener = null;
    }
    const port = this._getMessagePort();
    if (port && this._portMessageHandler && port.onbsmessage === this._portMessageHandler) {
      try {
        port.onbsmessage = this._previousOnbsmessage || null;
      } catch (_) { /* noop */ }
    }
    this._portMessageHandler = null;
    this._previousOnbsmessage = null;
  }

  /**
   * Route an incoming socket `deviceCommand` event to the correct
   * translator and either forward it to BrightScript or ACK it locally.
   */
  _handleDeviceCommand(data) {
    try {
      if (!data || typeof data !== 'object') {
        this._log('warn', 'ignoring deviceCommand without data', { data });
        return;
      }
      const commandId = data.commandId || data.id || null;
      const rawCommand = normalizeCommandName(data.command);
      const payload = data.payload || {};

      if (!rawCommand) {
        this._log('warn', 'ignoring deviceCommand without command name', { commandId });
        this._emitAck(commandId, 'failed', {
          error: 'missing_command_name',
        });
        return;
      }

      const translator = DEVICE_COMMAND_MAP[rawCommand];
      if (!translator) {
        this._log('info', 'unsupported command', { command: rawCommand, commandId });
        this._emitAck(commandId, 'failed', {
          command: rawCommand,
          error: 'unsupported_command_type',
          note: 'brightsign dispatcher has no mapping for this command',
        });
        return;
      }

      let mapping;
      try {
        mapping = translator(payload);
      } catch (err) {
        this._log('error', 'translator threw', { command: rawCommand, commandId, err });
        this._emitAck(commandId, 'failed', {
          command: rawCommand,
          error: 'translator_error',
          message: (err && err.message) || String(err),
        });
        return;
      }

      if (!mapping || typeof mapping !== 'object') {
        this._emitAck(commandId, 'failed', {
          command: rawCommand,
          error: 'translator_returned_invalid_mapping',
        });
        return;
      }

      if (mapping.kind === 'immediate') {
        this._emitAck(commandId, mapping.status || 'acked', mapping.details || {});
        return;
      }

      if (mapping.kind === 'unsupported') {
        this._emitAck(commandId, 'failed', {
          command: rawCommand,
          error: 'unsupported_command_type',
          reason: mapping.reason || 'unsupported_on_brightsign',
        });
        return;
      }

      if (mapping.kind === 'forward') {
        this._forwardToBridge(commandId, rawCommand, mapping);
        return;
      }

      this._emitAck(commandId, 'failed', {
        command: rawCommand,
        error: 'unknown_mapping_kind',
        kind: mapping.kind,
      });
    } catch (err) {
      this._log('error', 'unhandled error processing deviceCommand', { err });
    }
  }

  /**
   * Send a message via BSMessagePort and register the pending ACK
   * tracker. The BrightScript host echoes back an `{type:'ack', id, ...}`
   * payload which `_handleInboundMessage` matches by id.
   */
  _forwardToBridge(commandId, commandName, mapping) {
    const port = this._getMessagePort();
    if (!port || typeof port.PostBSMessage !== 'function') {
      this._log('warn', 'no BSMessagePort available; cannot forward', { commandId, commandName });
      this._emitAck(commandId, 'failed', {
        command: commandName,
        error: 'bridge_unavailable',
      });
      return;
    }

    // bridge.brs expects { type, id, ...} — clone the mapping.message
    // and inject the id so the ACK can be correlated. Messages without
    // a commandId still fire (one-way broadcast), but we cannot track
    // their ACK so we emit success immediately.
    const outbound = Object.assign({}, mapping.message, {
      id: commandId || '',
    });

    let postOk = true;
    try {
      port.PostBSMessage(JSON.stringify(outbound));
    } catch (err) {
      postOk = false;
      this._log('error', 'PostBSMessage threw', { commandId, commandName, err });
    }

    if (!postOk) {
      this._emitAck(commandId, 'failed', {
        command: commandName,
        error: 'post_bs_message_failed',
      });
      return;
    }

    // If the backend didn't provide a commandId we can still fire the
    // message but we cannot correlate an ACK. Emit success-on-post so
    // the portal status transitions correctly.
    if (!commandId) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!this._pending.has(commandId)) return;
      this._pending.delete(commandId);
      this._emitAck(commandId, 'failed', {
        command: commandName,
        error: 'bridge_timeout',
        timeoutMs: this._timeoutMs,
      });
    }, this._timeoutMs);

    this._pending.set(commandId, {
      commandName,
      successDetails: mapping.successDetails || { command: commandName },
      timeout,
      sentAt: Date.now(),
    });
  }

  /**
   * Handle a message arriving from BrightScript. Only reacts to `ack`
   * messages; other types (fps, deviceInfo, console) are ignored here —
   * bootstrap.html / BrightSignBridge own those channels.
   */
  _handleInboundMessage(rawMessage) {
    if (rawMessage == null) return;
    let message = rawMessage;
    // bridge.brs can send either a JSON string (when the JS side posts
    // a stringified payload and BrightScript echoes it back) or an
    // associative array that BrightScript serializes itself. Normalize.
    if (typeof message === 'string') {
      try {
        message = JSON.parse(message);
      } catch (_) {
        return; // non-JSON strings aren't ACKs
      }
    }
    if (!message || typeof message !== 'object') return;
    if (message.type !== 'ack') return;
    const id = message.id || message.commandId;
    if (!id) return;

    const pending = this._pending.get(id);
    if (!pending) {
      // Unknown command id — ignore. This protects against replays and
      // ACKs that arrive after the 30s timeout already fired.
      this._log('info', 'ignoring ack for unknown commandId', { commandId: id });
      return;
    }
    this._pending.delete(id);
    if (pending.timeout) {
      try { clearTimeout(pending.timeout); } catch (_) { /* noop */ }
    }

    const ok = message.ok === true;
    const result = message.result;
    const details = Object.assign({}, pending.successDetails, {
      bridgeResult: result == null ? null : result,
      bridgeOk: ok,
    });
    if (message.payload != null) {
      details.bridgePayload = message.payload;
    }
    this._emitAck(id, ok ? 'acked' : 'failed', details);
  }

  /**
   * Send `deviceCommandAck` back to the backend. The exact payload shape
   * matches the one the Tizen / Android agents emit (see
   * trillboard-api/socket.js `socket.on('deviceCommandAck', ...)`).
   * Accepts a null commandId — in that case we log and drop, since the
   * backend will reject it anyway.
   */
  _emitAck(commandId, status, details) {
    if (!commandId) {
      this._log('warn', 'cannot emit ack without commandId', { status, details });
      return;
    }
    if (!this._socket || typeof this._socket.emit !== 'function') {
      this._log('warn', 'no socket attached; dropping ack', { commandId, status });
      return;
    }
    const payload = {
      commandId,
      status,
      details: details || {},
      timestamp: new Date().toISOString(),
    };
    try {
      this._socket.emit('deviceCommandAck', payload);
    } catch (err) {
      this._log('error', 'socket.emit deviceCommandAck threw', { commandId, err });
    }
  }

  /**
   * Number of commands currently awaiting a BrightScript ACK. Exposed
   * for tests and ops dashboards.
   */
  pendingCount() {
    return this._pending.size;
  }
}

/**
 * Poll for `window.trillboardsSocket` until it appears or the timeout
 * elapses. Returns a Promise that resolves with the socket or null.
 * The overlay/main.js path initializes the socket asynchronously after
 * fingerprint derivation, so the dispatcher has to wait for it — we do
 * NOT use useEffect or a global side-effect; we poll explicitly.
 *
 * Note: this is NOT exponential backoff. It's a fixed-interval poll
 * bounded by SOCKET_POLL_MAX_MS so the dispatcher gives up fast if the
 * overlay never starts (e.g. in a unit test without main.js loaded).
 */
function waitForSocket(options) {
  const opts = options || {};
  const interval = typeof opts.intervalMs === 'number' ? opts.intervalMs : SOCKET_POLL_INTERVAL_MS;
  const maxWait = typeof opts.maxWaitMs === 'number' ? opts.maxWaitMs : SOCKET_POLL_MAX_MS;
  const getSocket = typeof opts.getSocket === 'function'
    ? opts.getSocket
    : () => (typeof window !== 'undefined' ? window.trillboardsSocket : null);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const sock = getSocket();
      if (sock) {
        resolve(sock);
        return;
      }
      if (Date.now() - startedAt >= maxWait) {
        resolve(null);
        return;
      }
      setTimeout(tick, interval);
    };
    tick();
  });
}

/**
 * Default installer — creates a singleton dispatcher and wires it into
 * whatever socket is currently (or soon-to-be) exposed on
 * `window.trillboardsSocket`. Returns the dispatcher instance so
 * bootstrap.js can retain it (but nothing forces retention).
 *
 * Idempotent: if a dispatcher is already installed on this window, the
 * existing one is returned without creating a second subscription.
 */
function installBrightSignDeviceCommandDispatcher(options) {
  if (typeof window === 'undefined') return null;
  if (window.__TRILL_BS_DEVICE_COMMAND_DISPATCHER__) {
    // Socket may have changed (overlay/main.js re-inits on reconnect).
    // If so, rebind the dispatcher to the new socket instance.
    const cached = window.__TRILL_BS_DEVICE_COMMAND_DISPATCHER__;
    if (window.trillboardsSocket && window.trillboardsSocket !== cached._socket) {
      cached.attach(window.trillboardsSocket);
    }
    return cached;
  }
  const dispatcher = new BrightSignDeviceCommandDispatcher(options);
  window.__TRILL_BS_DEVICE_COMMAND_DISPATCHER__ = dispatcher;

  // Attach immediately if the socket is already available; otherwise
  // poll for it. The poll returns a promise that we swallow with
  // .catch(noop) so a rejection here never crashes the React bundle.
  const attachNow = typeof window.trillboardsSocket !== 'undefined' && window.trillboardsSocket;
  if (attachNow) {
    dispatcher.attach(window.trillboardsSocket);
  } else {
    waitForSocket(options)
      .then((sock) => {
        if (sock) dispatcher.attach(sock);
      })
      .catch(() => { /* noop — fire-and-forget */ });
  }
  return dispatcher;
}

export {
  BrightSignDeviceCommandDispatcher,
  installBrightSignDeviceCommandDispatcher,
  waitForSocket,
  DEVICE_COMMAND_MAP,
  BRIDGE_TIMEOUT_MS,
  SOCKET_POLL_INTERVAL_MS,
  SOCKET_POLL_MAX_MS,
  // exported for white-box testing of the pure translators
  normalizeCommandName,
  clampLevel,
  normalizeInputState,
};
